import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import CommercialCombinedBusiness from '@/website/components/NewLandingPages/commercialCombinedBusiness/index';
import TopPromotionCountDownView from '@/website/components/NewLandingPages/components/topPromotionCountDown/common';
import { getRequestLanguage } from '@/website/constants/locales';
import Layout from '@/website/layouts/newLandingPages';
import { ContentfulPageProps, getContentfulData } from '@/website/lib/getContentfulData';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { buildCanonicalUrl } from '@/website/util/canonical';
import { parseServerSideCookies } from '@/website/util/cookies';
import { getCommercialCombinedBusinessWebsiteCrawlerHeadData } from '@/website/util/crawler';
import { businessInsuranceKey, getBusinessInsuranceLandingPageThemeMap } from '@jove-frontend/uikit';
import { Box } from '@mui/material';

const SLUG = 'commercial-combined-business';
export async function getServerSideProps(context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<ContentfulPageProps>> {
  const language = getRequestLanguage(context.locale, context.req.headers.cookie);

  return getContentfulData(context, SLUG, {
    metaInfo: {
      title: getCommercialCombinedBusinessWebsiteCrawlerHeadData(language)?.title,
      description: getCommercialCombinedBusinessWebsiteCrawlerHeadData(language)?.description,
      images: '/commercial-combined-business-landing.png',
    },
    useNewLogic: true,
    dataFileName: 'commercial-combined-business-landing-data.json',
    activeLanguage: language,
  });
}

const SlugCommercialCombinedBusinessLandingPage = (props: ContentfulPageProps) => {
  const { post, promotion, metadata } = props;
  const router = useRouter();
  const {
    state: { hidePromotionLandingPageHeader, hidePromotionLandingPageFooter },
    actions: { updatePromotionDetailInfo },
  } = promotionInfoStore();

  /**
   * effects
   */
  useEffect(() => {
    updatePromotionDetailInfo(promotion);
  }, [promotion]);

  return (
    <>
      <Head>
        <title>{metadata?.openGraph?.title}</title>
        <meta name="description" content={metadata?.openGraph?.description} />
        <link
          key="canonical"
          rel="canonical"
          href={buildCanonicalUrl(router.asPath, process.env['NX_WEBSITE_URL'], router.locale, router.query.country as string)}
        />
        <meta content={metadata?.openGraph?.title} property="og:title" key="og-title" />
        <meta content={metadata?.openGraph?.description} property="og:description" key="og-description" />
        <meta name="twitter:title" content={metadata?.openGraph?.title} />
        <meta name="twitter:description" content={metadata?.openGraph?.description} />
      </Head>
      <Layout.Wrapper>
        <TopPromotionCountDownView />
        {!hidePromotionLandingPageHeader && (
          <Layout.Header
            customLogoUrl={promotion?.brandingLogo}
            promotion={promotion}
            insuranceType={businessInsuranceKey}
            productScheme={getBusinessInsuranceLandingPageThemeMap().commercial.value}
          />
        )}
        <Layout.Content>
          <Box className="flex flex-col space-y-16" sx={{ backgroundColor: promotion?.brandingData?.primary_colour || '#EBEFFE' }}>
            <CommercialCombinedBusiness useNewLogic info={post} promotion={promotion} />
          </Box>
        </Layout.Content>
        {!hidePromotionLandingPageFooter && <Layout.Footer />}
      </Layout.Wrapper>
    </>
  );
};

export default SlugCommercialCombinedBusinessLandingPage;
